import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { TeoriKategori } from '../types'
import { allaTeoriposter, KATEGORI_ETIKETT, TYP_ETIKETT } from '../lib/teorisektion'
import { useSidTitel } from '../lib/seo'

/** Uppslagssidan för teorisektionen: alla modeller och teorier A–Ö med
 *  kategorifilter och fritextsök. Varje kort visar namn, kärnpoäng och
 *  miniatyrfigur; posterna har egna sidor på /modeller/<id>. */
export default function ModellerOversikt() {
  useSidTitel('Modeller och teorier A–Ö')
  const [filter, setFilter] = useState('')
  const [kategori, setKategori] = useState<TeoriKategori | 'alla'>('alla')

  const soktext = filter.trim().toLowerCase()
  const poster = allaTeoriposter
    .filter((p) => kategori === 'alla' || p.kategori === kategori)
    .filter(
      (p) =>
        !soktext ||
        p.namn.toLowerCase().includes(soktext) ||
        p.karnpoang.toLowerCase().includes(soktext) ||
        (p.upphov ?? '').toLowerCase().includes(soktext),
    )

  // Bara kategorier som faktiskt förekommer blir filterknappar.
  const kategorier = [...new Set(allaTeoriposter.map((p) => p.kategori))].sort((a, b) =>
    KATEGORI_ETIKETT[a].localeCompare(KATEGORI_ETIKETT[b], 'sv'),
  )

  return (
    <div className="wrap">
      <section className="hero">
        <span className="hero-etikett">Uppslagsverk · {allaTeoriposter.length} poster</span>
        <h1>Modeller och teorier</h1>
        <p>
          Läromedlets alla modeller, teorier och metoder i korta uppslagsposter — kärnan i en
          mening, en påminnelse om vad modellen säger och en hänvisning till kapitlet där den
          förklaras på djupet. Varje post redovisar sina källor.
        </p>
        <div className="hero-knappar">
          <Link className="knapp knapp-sekundar" to="/modeller/jamfor">
            ⇄ Jämför två modeller
          </Link>
        </div>
        <input
          type="search"
          className="begrepp-filter no-print"
          placeholder="Sök modell eller teori …"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filtrera modell- och teorilistan"
        />
      </section>

      <div className="kategorifilter no-print" role="group" aria-label="Filtrera på kategori">
        <button type="button" className={kategori === 'alla' ? 'aktiv' : ''} onClick={() => setKategori('alla')}>
          Alla
        </button>
        {kategorier.map((k) => (
          <button key={k} type="button" className={kategori === k ? 'aktiv' : ''} onClick={() => setKategori(k)}>
            {KATEGORI_ETIKETT[k]}
          </button>
        ))}
      </div>

      {poster.length === 0 && <p className="sektionsingress">Ingen post matchar sökningen.</p>}

      <div className="teorirutnat">
        {poster.map((p) => (
          <Link key={p.teori_id} to={`/modeller/${p.teori_id}`} className="teorikort">
            <div className="teorikort-meta">
              <span className={`typ-badge typ-${p.typ}`}>{TYP_ETIKETT[p.typ]}</span>
              <span className="teorikort-kategori">{KATEGORI_ETIKETT[p.kategori]}</span>
            </div>
            <h2>{p.namn}</h2>
            <p>{p.karnpoang}</p>
            {p.illustration && (
              <img className="teorikort-figur" src={p.illustration.fil} alt="" loading="lazy" />
            )}
          </Link>
        ))}
      </div>

      <p className="lararhandledning-blockbibliotek-lank">
        <Link to="/kallor">Samlad källförteckning →</Link>
        <span> Källorna som modellerna och teorierna vilar på.</span>
      </p>
    </div>
  )
}
